import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Eye, SortDownArrow } from "../../assets/svg";
import Breadcrumb from "../../components/common/BreadCrumb";
import {
  ExportButton,
  SelectWithLabel,
  Tooltip,
} from "../../components/micro/input";
import CustomTable from "../../components/customTable/CustomTable";
import { getCompanyReportList } from "../../redux/reports/slice";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/loader/RippleLoader";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import ViewService from "./ViewService";

const CompaniesReports = () => {
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [dateRange, setDateRange] = useState([null, null]);
  const [viewServicePopup, setViewServicePopup] = useState(false);
  const [viewServiceData, setViewServiceData] = useState({});
  const [servicePageCount, setServicePageCount] = useState(1);

  const DateRange =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
          dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "";

  // Selector
  const { getCompanyReportListData, loading } = useSelector((store) => ({
    getCompanyReportListData:
      store?.salesReportData?.getCompanyReportListStatus?.data,
    loading: store?.salesReportData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getCompanyReportList({
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        date_range: DateRange || "",
        year: year || "",
        service_page: servicePageCount || "",
        service_limit: "",
      })
    );
  }, [
    pageCount,
    pageLimit,
    sortBy,
    sortDirection,
    DateRange,
    year,
    servicePageCount,
  ]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    label: (currentYear - i).toString(),
    value: (currentYear - i).toString(),
  }));

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  const PieChartOptions = {
    chart: {
      type: "pie",
    },
    labels: ["Recurring", "Repair", "Follow Up Repair"],
    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },
    colors: ["#F15B29", "#2E2E2E", "#FF9800"],
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const count = opts.w.config.series[opts.seriesIndex];
        return `${count}`;
      },
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
  };

  const LineChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      curve: "straight",
      width: 2,
    },
    xaxis: {
      categories: getCompanyReportListData?.data?.service_request?.map((item) =>
        item?.month?.slice(0, 3)
      ),
    },
    colors: ["#F15B29", "#2E2E2E", "#FF9800"],
    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },
  };

  const pieSeries = [
    getCompanyReportListData?.data?.recurring_service_request_count || 0,
    getCompanyReportListData?.data?.repair_service_request_count || 0,
    getCompanyReportListData?.data?.follow_up_service_request_count || 0,
  ];

  const lineSeries = [
    {
      name: "Recurring",
      data: getCompanyReportListData?.data?.service_request?.map(
        (item) => item?.recurrig_services || 0
      ),
    },
    {
      name: "Repair",
      data: getCompanyReportListData?.data?.service_request?.map(
        (item) => item?.repair_services || 0
      ),
    },
    {
      name: "Follow Up Repair",
      data: getCompanyReportListData?.data?.service_request?.map(
        (item) => item?.follow_up_services || 0
      ),
    },
  ];

  const handleSalesRevenueExportClick = () => {
    const revenueData = getCompanyReportListData?.data?.service_request;

    if (!Array.isArray(revenueData) || revenueData.length === 0) {
      alert("No chart data available to export!");
      return;
    }

    const header = ["Month", "Recurring Services", "Repair Services"];
    const rows = revenueData.map((item) => [
      `"${item?.month || "-"}"`,
      `"${item?.recurrig_services || 0}"`,
      `"${item?.single_services || 0}"`,
    ]);

    let csvContent =
      header.join(",") + "\n" + rows.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "service_revenue_chart.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSalesBreakdownExportClick = () => {
    const salesData = getCompanyReportListData?.data?.request_types?.data;

    if (!Array.isArray(salesData) || salesData.length === 0) {
      alert("No sales data available to export!");
      return;
    }

    const header = [
      "No.",
      "Company Name",
      "Service ID",
      "Request Type",
      "Date",
    ];

    const rows = salesData.map((item, index) => [
      `"${index + 1}"`,
      `"${item?.company_name || "-"}"`,
      `"${item?.unique_id || "-"}"`,
      `"${item?.request_type || "-"}"`,
      `"${item?.date || "-"}"`,
    ]);

    let csvContent =
      header.join(",") + "\n" + rows.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "service_breakdown.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const Revenue = () => {
    return (
      <div className="grid grid-cols-10 gap-6">
        {/* Pie Chart Box */}
        <div className="col-span-10 md:col-span-3 bg-white rounded-xl p-6 shadow border border-borderLine flex flex-col">
          <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
            Request Type
          </h2>
          <p className="text-sm text-gray-dark mb-4">
            Total:{" "}
            <span className="font-bold text-lg">
              {getCompanyReportListData?.data?.Total_services}
            </span>{" "}
            Service Request Number
          </p>
          <Chart
            options={PieChartOptions}
            series={pieSeries}
            type="pie"
            height={250}
          />
        </div>

        {/* Line Chart Box */}
        <div className="col-span-10 md:col-span-7 bg-white rounded-xl p-6 shadow border border-borderLine">
          <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
            Request Type
          </h2>
          <p className="text-sm text-gray-dark mb-4">Types of Servicing</p>
          <Chart
            options={LineChartOptions}
            series={lineSeries}
            type="line"
            height={250}
          />
        </div>
      </div>
    );
  };

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <span>No.</span>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company_name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company_name"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("unique_id");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          Recurring
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "unique_id"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("request_type");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Repair</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "request_type"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Follow Up Repair</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Total Count</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <span>Action</span>
      </th>
    </>
  );

  const DataRows = () => {
    return getCompanyReportListData?.data?.request_types?.data?.map(
      (item, index) => (
        <tr
          key={item?.id || index}
          className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="p-4 text-sm text-primaryBlack">{index + 1}</td>
          <td className="p-4 text-sm text-primaryBlack">
            {item?.company_name || "-"}
          </td>
          <td className="p-4 text-sm ">
            {item?.recurring_service_request_count || "-"}
          </td>
          <td className="p-4 text-sm text-primaryBlack">
            {item?.repair_service_request_count || "-"}
          </td>
          <td className="p-4 text-sm text-primaryBlack">
            {item?.follow_up_service_request_count || "-"}
          </td>
          <td className="px-4 py-2">{item?.Total_count}</td>
          <td className="px-4 py-2">
            <div className="flex items-center space-x-3">
              <Tooltip
                label={
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setViewServicePopup(true);
                        setViewServiceData(item);
                      }}
                    >
                      <Eye />
                    </button>
                  </>
                }
                tooltipLabel={
                  <>
                    <div className="whitespace-nowrap ">
                      <p>View </p>
                    </div>
                  </>
                }
              />
            </div>
          </td>
        </tr>
      )
    );
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Company Service Reports
            </h6>
            <Breadcrumb
              breadCrumbParent="Home"
              breadCrumbParent2="Reports"
              breadCrumbActive="Company Service Report"
              breadCrumbTitleKey="/"
              classes=""
            />
          </div>
        </div>
        {loading ? (
          <RippleLoader />
        ) : (
          <>
            <div className="flex justify-between items-center mt-6">
              <h2 className="text-2xl font-normal font-Montserrat text-primaryBlacks">
                Service Report
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {/* Year Dropdown */}
                <div className="w-full sm:w-[100px] md:w-[120px] min-w-[90px]">
                  <SelectWithLabel
                    className="w-full py-2"
                    placeholder="Year"
                    options={yearOptions}
                    value={year}
                    onChange={handleYearChange}
                  />
                </div>

                {/* Export Button */}
                <div className="w-full sm:w-auto -mt-1">
                  <ExportButton
                    value="EXPORT CSV"
                    onClick={handleSalesRevenueExportClick}
                  />
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Revenue />
            </div>
            <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-6">
              <div>
                <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                  Total Count:{" "}
                  <span className="font-bold text-lg">
                    {getCompanyReportListData?.data?.Total_services}
                  </span>
                </h2>
              </div>
              <div>
                <p className="text-sm text-gray-dark">
                  Recurring:{" "}
                  {
                    getCompanyReportListData?.data
                      ?.recurring_service_request_count
                  }
                </p>
                <p className="text-sm text-gray-dark">
                  Repair:{" "}
                  {getCompanyReportListData?.data?.repair_service_request_count}
                </p>
                <p className="text-sm text-gray-dark">
                  Follow Up Repair:{" "}
                  {
                    getCompanyReportListData?.data
                      ?.follow_up_service_request_count
                  }
                </p>
              </div>
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                  Overview Breakdown
                </h2>
                <div className="flex items-center gap-2">
                  <div className="w-full sm:w-[220px] md:w-[240px] -mt-1 min-w-[160px]">
                    <div className="text-black border max-w-64 border-borderTheme/80  flex justify-between relative   plus-number-inputs rounded w-full px-2 py-[7px] items-center   transition duration-300 ease-in-out">
                      <ReactDatePicker
                        selected={dateRange[0]}
                        onChange={(dates) => setDateRange(dates)}
                        selectsRange
                        isClearable={true}
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        maxDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Date Range"
                        className="w-full"
                      />
                    </div>
                  </div>
                  <ExportButton
                    value="EXPORT CSV"
                    className="-mt-1"
                    onClick={handleSalesBreakdownExportClick}
                  />
                </div>
              </div>
              <CustomTable
                overflowVisibleLg={true}
                columnHeaders={<ColumnHeaders />}
                dataRows={<DataRows />}
                data={getCompanyReportListData?.data?.request_types}
                loading={false}
                showPagination={true}
                setPaginationCurrentLimit={setPageLimit}
                paginationCurrentLimit={pageLimit}
                current={pageCount}
                total={getCompanyReportListData?.data?.request_types?.total}
                paginationData={(crPage) => setPageCount(crPage)}
                lastPage={Math.ceil(
                  getCompanyReportListData?.data?.request_types?.total /
                    pageLimit
                )}
              />
            </div>
          </>
        )}
      </div>
      {viewServicePopup && (
        <div>
          <ViewService
            initialServiceData={viewServiceData}
            setViewServicePopup={setViewServicePopup}
          />
        </div>
      )}
    </>
  );
};

export default CompaniesReports;
